// Theme preference (system | light | dark), persisted in localStorage and
// applied by toggling a `dark` class on <html>. The CSS variables in
// styles.css invert under `.dark`. No-flash init: the webapp's index.html has
// an inline script that applies this before paint; the extension entries call
// applyTheme() at module top (MV3 CSP blocks inline scripts there).

export type ThemePref = 'system' | 'light' | 'dark';

export const THEME_KEY = 'tn-theme';
const THEME_EVENT = 'tn-theme-changed';

export function getThemePref(): ThemePref {
  try {
    const v = localStorage.getItem(THEME_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    /* localStorage unavailable — fall through to default */
  }
  return 'system';
}

export function resolveTheme(pref: ThemePref = getThemePref()): 'light' | 'dark' {
  if (pref !== 'system') return pref;
  return typeof matchMedia !== 'undefined' &&
    matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

export function applyTheme(pref: ThemePref = getThemePref()): void {
  if (typeof document === 'undefined') return;
  const dark = resolveTheme(pref) === 'dark';
  const root = document.documentElement;
  root.classList.toggle('dark', dark);
  root.style.colorScheme = dark ? 'dark' : 'light';
}

export function setThemePref(pref: ThemePref): void {
  try {
    localStorage.setItem(THEME_KEY, pref);
  } catch {
    /* ignore */
  }
  applyTheme(pref);
  try {
    window.dispatchEvent(new CustomEvent(THEME_EVENT, { detail: pref }));
  } catch {
    /* ignore */
  }
}

// Re-apply when the OS appearance changes, but only while following system.
export function watchSystem(): void {
  if (typeof matchMedia === 'undefined') return;
  matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (getThemePref() === 'system') applyTheme('system');
  });
}

// Subscribe to in-session preference changes (keeps the header toggle and the
// Settings control in sync). Returns an unsubscribe fn.
export function onThemeChange(cb: (pref: ThemePref) => void): () => void {
  const handler = (e: Event) => cb((e as CustomEvent).detail as ThemePref);
  window.addEventListener(THEME_EVENT, handler);
  return () => window.removeEventListener(THEME_EVENT, handler);
}
